import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { AuthApi } from '@features/auth/data-access/auth-api.port';
import {
  AuthUserDto,
  LoginResponseDto,
  GraphqlCreateUserResponseDto,
} from '@features/auth/data-access/dto/auth.dto';
import {
  mapAuthUserDtoToDomain,
  mapCreateUserPayloadToLoginResult,
  mapLoginResponseDtoToDomain,
} from '@features/auth/data-access/mappers/auth.mapper';
import {
  LoginCredentials,
  LoginResult,
  RegisterCredentials,
  RequestUserEmailOtpResult,
  ResetPasswordInput,
  VerifyUserEmailOtpResult,
} from '@features/auth/models/auth.models';
import { SessionUser } from '@core/auth/auth-session.model';

const NEW_USER_MUTATION = `
  mutation CreateUser(
    $email: String!
    $password: String!
    $name: String!
    $lastName: String
    $businessName: String
  ) {
    createUser(
      email: $email
      password: $password
      name: $name
      lastName: $lastName
      businessName: $businessName
    ) {
      user {
        id
        email
        name
        lastName
        emailVerifiedAt
        emailVerified
      }
      token
      refreshToken
      errors
    }
  }
`;

const REQUEST_USER_EMAIL_OTP_MUTATION = `
  mutation RequestUserEmailOtp {
    requestUserEmailOtp {
      sent
      errors
      retryAfterSeconds
    }
  }
`;

const VERIFY_USER_EMAIL_OTP_MUTATION = `
  mutation VerifyUserEmailOtp($code: String!) {
    verifyUserEmailOtp(code: $code) {
      user {
        id
        email
        name
        lastName
        fullName
        roles
        emailVerifiedAt
        emailVerified
      }
      errors
    }
  }
`;

interface GraphqlOtpRequestResponse {
  data?: {
    requestUserEmailOtp?: {
      sent?: boolean;
      errors?: string[] | null;
      retryAfterSeconds?: number | null;
    } | null;
  } | null;
  errors?: Array<{ message?: string }>;
}

interface GraphqlOtpVerifyResponse {
  data?: {
    verifyUserEmailOtp?: {
      user?: {
        id: string | number;
        email: string;
        name?: string | null;
        lastName?: string | null;
        fullName?: string | null;
        roles?: string[] | null;
        emailVerifiedAt?: string | null;
        emailVerified?: boolean | null;
      } | null;
      errors?: string[] | null;
    } | null;
  } | null;
  errors?: Array<{ message?: string }>;
}

@Injectable()
export class AuthApiService implements AuthApi {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  login(credentials: LoginCredentials): Observable<LoginResult> {
    return this.http
      .post<LoginResponseDto>(`${this.baseUrl}${ApiEndpoints.auth.login}`, credentials)
      .pipe(map((dto) => mapLoginResponseDtoToDomain(dto)));
  }

  me(): Observable<LoginResult['user']> {
    return this.http
      .get<{ user: AuthUserDto }>(`${this.baseUrl}${ApiEndpoints.auth.me}`)
      .pipe(map((body) => mapAuthUserDtoToDomain(body.user)));
  }

  register(credentials: RegisterCredentials): Observable<LoginResult> {
    return this.http
      .post<GraphqlCreateUserResponseDto>(`${this.baseUrl}${ApiEndpoints.graphql}`, {
        query: NEW_USER_MUTATION,
        variables: {
          email: credentials.email,
          password: credentials.password,
          name: credentials.name,
          lastName: credentials.lastName ?? '',
          businessName: credentials.businessName?.trim() || null,
        },
      })
      .pipe(
        map((body) => {
          if (body.errors?.length) {
            throw new Error('REGISTER_FAILED');
          }
          const payload = body.data?.createUser;
          if (!payload) {
            throw new Error('REGISTER_FAILED');
          }
          return mapCreateUserPayloadToLoginResult(payload);
        })
      );
  }

  loginWithGoogle(
    idToken: string,
    options?: { businessName?: string }
  ): Observable<LoginResult> {
    const businessName = options?.businessName?.trim() || null;
    return this.http
      .post<LoginResponseDto>(`${this.baseUrl}${ApiEndpoints.auth.googleOauth}`, {
        id_token: idToken,
        ...(businessName ? { business_name: businessName } : {}),
      })
      .pipe(map((dto) => mapLoginResponseDtoToDomain(dto)));
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http
      .post<{ message?: string }>(`${this.baseUrl}${ApiEndpoints.auth.forgotPassword}`, { email })
      .pipe(map(() => undefined));
  }

  resetPassword(input: ResetPasswordInput): Observable<void> {
    return this.http
      .post<{ message?: string }>(`${this.baseUrl}${ApiEndpoints.auth.resetPassword}`, {
        email: input.email,
        code: input.code,
        password: input.password,
        password_confirmation: input.passwordConfirmation,
      })
      .pipe(map(() => undefined));
  }

  requestUserEmailOtp(): Observable<RequestUserEmailOtpResult> {
    return this.http
      .post<GraphqlOtpRequestResponse>(`${this.baseUrl}${ApiEndpoints.graphql}`, {
        query: REQUEST_USER_EMAIL_OTP_MUTATION,
        operationName: 'RequestUserEmailOtp',
      })
      .pipe(
        map((body) => {
          if (body.errors?.length) {
            throw new Error('REQUEST_EMAIL_OTP_FAILED');
          }
          const payload = body.data?.requestUserEmailOtp;
          return {
            sent: payload?.sent === true,
            errors: payload?.errors ?? [],
            retryAfterSeconds:
              typeof payload?.retryAfterSeconds === 'number'
                ? payload.retryAfterSeconds
                : null,
          };
        })
      );
  }

  verifyUserEmailOtp(code: string): Observable<VerifyUserEmailOtpResult> {
    return this.http
      .post<GraphqlOtpVerifyResponse>(`${this.baseUrl}${ApiEndpoints.graphql}`, {
        query: VERIFY_USER_EMAIL_OTP_MUTATION,
        operationName: 'VerifyUserEmailOtp',
        variables: { code },
      })
      .pipe(
        map((body) => {
          if (body.errors?.length) {
            throw new Error('VERIFY_EMAIL_OTP_FAILED');
          }
          const payload = body.data?.verifyUserEmailOtp;
          const errors = payload?.errors ?? [];
          const raw = payload?.user;
          if (!raw || errors.length > 0) {
            return { user: null, errors: errors.length ? errors : ['VERIFY_FAILED'] };
          }
          const idRaw = raw.id;
          const parsed =
            typeof idRaw === 'string' ? Number.parseInt(idRaw, 10) : Number(idRaw);
          const user: SessionUser = {
            id: Number.isFinite(parsed) ? parsed : 0,
            email: raw.email,
            name:
              [raw.fullName, [raw.name, raw.lastName].filter(Boolean).join(' ').trim(), raw.name]
                .find((v) => typeof v === 'string' && v.trim().length > 0)
                ?.trim() ?? raw.email,
            roles: raw.roles ?? [],
            emailVerifiedAt: raw.emailVerifiedAt ?? null,
            emailVerified: raw.emailVerified ?? Boolean(raw.emailVerifiedAt),
            companies: [],
            locations: [],
          };
          return { user, errors: [] };
        })
      );
  }
}
